import { isEmbedMessage, EmbedEventPayload } from '../types';

/**
 * One implementation of "would a lesson accept this message?", shared by the
 * slide that enforces it and the checker that reports on it.
 *
 * The checker exists because every failure in this protocol is silent — a
 * wrong origin, a malformed envelope and an app that simply never posts all
 * look identical from the outside: nothing happens. A checker that reimplemented
 * these rules would be worse than none, because it would hand out confidence
 * that does not transfer to a real lesson. So the rules live here and both
 * callers read them.
 */

/** Every event name the lesson currently understands. */
export const KNOWN_EMBED_EVENTS = [
  'ready', 'started', 'progress', 'completed', 'checkpoint', 'advance', 'submitted', 'error',
] as const;

export type KnownEmbedEvent = (typeof KNOWN_EMBED_EVENTS)[number];

/**
 * The origin a slide will accept messages from, derived exactly as the slide
 * derives it at runtime. Null for anything unparseable — most often a URL typed
 * without its scheme, which is itself a silent killer: the runtime check throws,
 * and every message is dropped with nothing surfaced anywhere.
 */
export function embedOriginFor(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

export type EmbedVerdict =
  /** Accepted — a lesson would act on this. */
  | { status: 'accepted'; event: KnownEmbedEvent; payload: EmbedEventPayload }
  /** Well-formed envelope, but an event name the lesson does not handle. */
  | { status: 'unknown-event'; event: string; payload: EmbedEventPayload }
  /** Right origin, but not a recognisable lms-embed-event. Silently ignored. */
  | { status: 'malformed'; reason: string }
  /** Correct shape, wrong sender. THE failure that wastes afternoons. */
  | { status: 'wrong-origin'; received: string; expected: string }
  /** The configured URL yielded no origin, so the slide drops everything. */
  | { status: 'no-expected-origin' };

/**
 * Classify one received message the way a webapp slide would.
 *
 * Order matters and mirrors the slide: origin is checked before shape, so a
 * correctly-formed message from the wrong origin reports as wrong-origin rather
 * than being lumped in with junk. That distinction is the whole point — it is
 * the difference between "your code is wrong" and "your code is fine, you are
 * serving it from somewhere the lesson will not listen to".
 */
export function classifyEmbedMessage(
  receivedOrigin: string,
  data: unknown,
  expectedOrigin: string | null,
): EmbedVerdict {
  // No derivable origin means the slide's own `new URL(...)` throws, and it
  // drops EVERY message. Reporting "accepted" here would be the precise drift
  // this module exists to prevent: a green result that does not survive contact
  // with a real lesson.
  if (!expectedOrigin) return { status: 'no-expected-origin' };

  if (receivedOrigin !== expectedOrigin) {
    return { status: 'wrong-origin', received: receivedOrigin, expected: expectedOrigin };
  }

  if (!isEmbedMessage(data)) {
    const d = data as { type?: unknown; version?: unknown } | null;
    const reason =
      d && typeof d === 'object' && 'type' in d && d.type !== 'lms-embed-event'
        ? `type was ${JSON.stringify(d.type)}, expected "lms-embed-event"`
        : d && typeof d === 'object' && 'version' in d && d.version !== '1.0'
          ? `version was ${JSON.stringify(d.version)}, expected "1.0"`
          : 'not an lms-embed-event envelope';
    return { status: 'malformed', reason };
  }

  const payload = data.payload;
  const event = (payload as { event?: string })?.event ?? '';

  if ((KNOWN_EMBED_EVENTS as readonly string[]).includes(event)) {
    return { status: 'accepted', event: event as KnownEmbedEvent, payload };
  }
  return { status: 'unknown-event', event, payload };
}

// ---------------------------------------------------------------------------
// Results — the optional `result` on a `completed` event
// ---------------------------------------------------------------------------

/** One question (or task) inside a result. */
export interface EmbedResultItem {
  /** Stable within the app: the same question keeps the same id across attempts. */
  id: string;
  earned: number;
  possible: number;
  /** Short human label, e.g. "Q1.3 — source reliability". */
  label?: string;
  /** The skill this item tests, for "is this skill improving?" reporting. */
  skill?: string;
}

/** A whole run-through's marks, as recorded in embed_results. */
export interface EmbedResult {
  /** Unique per run-through, chosen by the app. Missing → null (the slide fills one in). */
  attemptId: string | null;
  earned: number;
  possible: number;
  items: EmbedResultItem[] | null;
}

export type EmbedResultParse =
  | { ok: true; result: EmbedResult }
  | { ok: false; reason: string };

/** Bounds shared with record_embed_result (20261004000002). Keep them in step. */
export const EMBED_RESULT_LIMITS = {
  maxPossible: 1000,
  maxItems: 200,
  maxIdLength: 100,
  maxLabelLength: 200,
} as const;

const isMark = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/**
 * Validate a `completed` event's `result`. The one implementation, shared by
 * the slide (which records it) and the embed checker (which reports on it),
 * for the same reason as classifyEmbedMessage: a checker that drifts from the
 * slide hands out confidence that doesn't transfer to a real lesson.
 *
 * All-or-nothing on the totals, but a bad ITEM fails the whole result rather
 * than being dropped: a result with silently missing questions would report a
 * per-skill picture that doesn't add up to its own total.
 */
export function parseEmbedResult(raw: unknown): EmbedResultParse {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, reason: 'result must be an object' };
  }
  const r = raw as Record<string, unknown>;
  const L = EMBED_RESULT_LIMITS;

  if (!isMark(r.earned) || !isMark(r.possible)) {
    return { ok: false, reason: 'result.earned and result.possible must be numbers' };
  }
  if (r.possible <= 0 || r.possible > L.maxPossible) {
    return { ok: false, reason: `result.possible must be between 1 and ${L.maxPossible}` };
  }
  if (r.earned < 0 || r.earned > r.possible) {
    return { ok: false, reason: 'result.earned must be between 0 and result.possible' };
  }

  let attemptId: string | null = null;
  if (r.attemptId !== undefined && r.attemptId !== null) {
    if (typeof r.attemptId !== 'string' || !r.attemptId.trim() || r.attemptId.length > L.maxIdLength) {
      return { ok: false, reason: `result.attemptId must be a non-empty string of at most ${L.maxIdLength} characters` };
    }
    attemptId = r.attemptId;
  }

  let items: EmbedResultItem[] | null = null;
  if (r.items !== undefined && r.items !== null) {
    if (!Array.isArray(r.items)) return { ok: false, reason: 'result.items must be an array' };
    if (r.items.length > L.maxItems) return { ok: false, reason: `result.items has more than ${L.maxItems} entries` };
    items = [];
    for (let i = 0; i < r.items.length; i++) {
      const it = r.items[i] as Record<string, unknown> | null;
      const where = `result.items[${i}]`;
      if (!it || typeof it !== 'object') return { ok: false, reason: `${where} must be an object` };
      if (typeof it.id !== 'string' || !it.id.trim() || it.id.length > L.maxIdLength) {
        return { ok: false, reason: `${where}.id must be a non-empty string` };
      }
      if (!isMark(it.earned) || !isMark(it.possible) || it.possible <= 0 || it.earned < 0 || it.earned > it.possible) {
        return { ok: false, reason: `${where} needs earned and possible, with 0 ≤ earned ≤ possible` };
      }
      for (const key of ['label', 'skill'] as const) {
        if (it[key] !== undefined && (typeof it[key] !== 'string' || (it[key] as string).length > L.maxLabelLength)) {
          return { ok: false, reason: `${where}.${key} must be a string of at most ${L.maxLabelLength} characters` };
        }
      }
      items.push({
        id: it.id,
        earned: it.earned,
        possible: it.possible,
        ...(typeof it.label === 'string' ? { label: it.label } : {}),
        ...(typeof it.skill === 'string' ? { skill: it.skill } : {}),
      });
    }
  }

  return { ok: true, result: { attemptId, earned: r.earned, possible: r.possible, items } };
}

/**
 * The 0-100 integer embed_interactions.score holds. `upsert_embed_interaction`
 * takes p_score INTEGER, so an unrounded 85.5 would fail the whole tracking
 * call — completion included. Returns undefined when there's nothing usable.
 */
export function embedScorePercent(score: unknown, result?: EmbedResult | null): number | undefined {
  if (isMark(score)) return Math.round(Math.min(100, Math.max(0, score)));
  if (result) return Math.round((result.earned / result.possible) * 100);
  return undefined;
}

// ---------------------------------------------------------------------------
// Checkpoints — a `checkpoint` event mid-run, and the saved state sent back
// in on `lms-init`
// ---------------------------------------------------------------------------

/** Bounds on one checkpoint. `maxPerAttempt` is enforced by the slide. */
export const EMBED_CHECKPOINT_LIMITS = {
  maxStepLength: 100,
  /** JSON bytes of `data` once serialised. */
  maxDataBytes: 4096,
  maxPerAttempt: 50,
} as const;

/** One moment in a run-through, validated. */
export interface EmbedCheckpoint {
  /** Stable id for the moment: the same string every time it happens. */
  step: string;
  /** The run-through this belongs to. Null when the app sent none (the slide fills one in). */
  attemptId: string | null;
  /** Marks for this moment when it is assessed; same rules as a completed result. */
  result: Omit<EmbedResult, 'attemptId'> | null;
  /** What the learner chose or wrote: plain JSON, size-capped. */
  data: Record<string, unknown> | null;
}

export type EmbedCheckpointParse =
  | { ok: true; checkpoint: EmbedCheckpoint }
  | { ok: false; reason: string };

/**
 * Validate a `checkpoint` event. Shared by the slide (which records it) and
 * the checker (which reports on it), like parseEmbedResult and for the same
 * reason. An unassessed moment omits `result`; sending zeros is rejected
 * because `possible` must be positive wherever marks appear.
 */
export function parseEmbedCheckpoint(payload: unknown): EmbedCheckpointParse {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { ok: false, reason: 'checkpoint payload must be an object' };
  }
  const p = payload as Record<string, unknown>;
  const L = EMBED_CHECKPOINT_LIMITS;

  if (typeof p.step !== 'string' || !p.step.trim() || p.step.length > L.maxStepLength) {
    return { ok: false, reason: `checkpoint.step must be a non-empty string of at most ${L.maxStepLength} characters` };
  }

  let attemptId: string | null = null;
  if (p.attemptId !== undefined && p.attemptId !== null) {
    if (typeof p.attemptId !== 'string' || !p.attemptId.trim() || p.attemptId.length > EMBED_RESULT_LIMITS.maxIdLength) {
      return { ok: false, reason: `checkpoint.attemptId must be a non-empty string of at most ${EMBED_RESULT_LIMITS.maxIdLength} characters` };
    }
    attemptId = p.attemptId;
  }

  let result: EmbedCheckpoint['result'] = null;
  if (p.result !== undefined && p.result !== null) {
    const parsed = parseEmbedResult(p.result);
    if (!('result' in parsed)) return { ok: false, reason: `checkpoint.${parsed.reason}` };
    const { attemptId: _ignored, ...rest } = parsed.result;
    result = rest;
  }

  let data: Record<string, unknown> | null = null;
  if (p.data !== undefined && p.data !== null) {
    if (typeof p.data !== 'object' || Array.isArray(p.data)) {
      return { ok: false, reason: 'checkpoint.data must be a plain object' };
    }
    let json: string;
    try {
      json = JSON.stringify(p.data);
    } catch {
      return { ok: false, reason: 'checkpoint.data must be plain JSON' };
    }
    if (json === undefined) return { ok: false, reason: 'checkpoint.data must be plain JSON' };
    if (new TextEncoder().encode(json).length > L.maxDataBytes) {
      return { ok: false, reason: `checkpoint.data is over ${L.maxDataBytes} bytes of JSON; keep it to short strings, numbers and lists` };
    }
    data = JSON.parse(json) as Record<string, unknown>;
  }

  return { ok: true, checkpoint: { step: p.step, attemptId, result, data } };
}

/** The last checkpoint recorded for one (slide, step), as sent in `lms-init`. */
export interface EmbedStateCheckpoint {
  slideId: string;
  step: string;
  attemptId: string | null;
  result: Omit<EmbedResult, 'attemptId'> | null;
  data: Record<string, unknown> | null;
  /** ISO timestamp of the write. */
  recordedAt: string;
}

/** One completed run-through's marks, as sent in `lms-init`. */
export interface EmbedStateResult {
  slideId: string;
  attemptId: string;
  earned: number;
  possible: number;
  items: EmbedResultItem[] | null;
  recordedAt: string;
}

/**
 * What the lesson knows about this learner on this LESSON, sent back to the
 * app on `lms-init` so it can resume across devices and so a later slide can
 * read an earlier one (sitting B seeing sitting A). `checkpoints` holds the
 * latest per (slide, step) across every webapp slide in the lesson; `results`
 * holds every completed run-through, newest first. Null on the init message
 * means "unknown" (not enrolled, staff preview, or the read failed), and an
 * app must treat that exactly like an empty history.
 */
export interface EmbedState {
  checkpoints: EmbedStateCheckpoint[];
  results: EmbedStateResult[];
}

/** The learning_interactions row shape the state read selects. */
export interface EmbedCheckpointRow {
  slide_id: string;
  activity_key: string;
  response: Record<string, unknown> | null;
  server_ts: string;
}

/**
 * Collapse newest-first rows to the latest per (slide, step). Pure, so the
 * selection rule is testable without a database; the service only fetches.
 */
export function latestCheckpoints(rows: EmbedCheckpointRow[]): EmbedStateCheckpoint[] {
  const out: EmbedStateCheckpoint[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const key = `${row.slide_id}\u0000${row.activity_key}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const r = row.response ?? {};
    out.push({
      slideId: row.slide_id,
      step: row.activity_key,
      attemptId: typeof r.attemptId === 'string' ? r.attemptId : null,
      result: r.result && typeof r.result === 'object' ? (r.result as EmbedStateCheckpoint['result']) : null,
      data: r.data && typeof r.data === 'object' ? (r.data as Record<string, unknown>) : null,
      recordedAt: row.server_ts,
    });
  }
  return out;
}

/** Correctness for a checkpoint's marks, in the framework's own terms. */
export function checkpointCorrectness(result: EmbedCheckpoint['result']): 'correct' | 'incorrect' | 'partial' | 'n/a' {
  if (!result) return 'n/a';
  if (result.earned >= result.possible) return 'correct';
  if (result.earned <= 0) return 'incorrect';
  return 'partial';
}
